import { createRng } from '../rng/seededRandom';
import { forwardVector } from './physics';
import type { TileMap } from './tilemap';
import type { AttackType } from '../characters/types';
import {
  fighterOf,
  type BattleEvent,
  type BattleState,
  type Fighter,
  type FighterId,
  type FighterInput,
  type HitSource,
  type Projectile,
  type Vec3,
} from './types';

export const PROJECTILE_SPEED = 24;
export const PROJECTILE_TTL = 2.5;
export const PROJECTILE_RADIUS = 0.25;
/** A held stick may bend magic by at most 35 degrees, quickly enough to correct but not U-turn. */
export const PROJECTILE_STEER_LIMIT = (35 * Math.PI) / 180;
export const PROJECTILE_STEER_RATE = (90 * Math.PI) / 180;
/** Seconds a blow staggers its target. Nothing else staggers (D-34). */
export const HIT_STUN = 0.25;
export const MELEE_ARC = (100 * Math.PI) / 180;
export const BURST_SELF_DAMAGE = 0.05;

export function eyePosition(f: Fighter): Vec3 {
  return { x: f.pos.x, y: f.pos.y + f.height * 0.9, z: f.pos.z };
}

export function centre(f: Fighter): Vec3 {
  return { x: f.pos.x, y: f.pos.y + f.height / 2, z: f.pos.z };
}

export function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

/**
 * Contact weapons — melee, aura and burst — reach across the ground plane rather than through
 * three dimensions. With one walkable plane (D-17) a flier's altitude is a rendering detail, and
 * measuring through it would make anything that hovers immune to everything that walks.
 */
export function horizontalDistance(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/** Whether a tile stands between two points, judged against the lower of the two (see `blocksSight`). */
export function sightBlocked(map: TileMap, a: Vec3, b: Vec3): boolean {
  return map.blocksSight(a.x, a.z, b.x, b.z, Math.min(a.y, b.y));
}

/** The seat a fighter is fighting: the other living one. v1 has two; the state allows six. */
export function opponentOf(state: BattleState, id: FighterId): FighterId {
  const others = state.seats.filter((seat) => seat !== id);
  return others.find((seat) => fighterOf(state, seat).alive) ?? others[0] ?? id;
}

/**
 * Seconds between pressing a blow, an aura or a burst and its landing (D-42, THE_DECISION_TABLE
 * #2): long enough for the arena to show it coming and a quick opponent to step out of it, short
 * enough to keep the duel's pace. A shot leaves at once, since its flight is its warning.
 */
export const WIND_UP = 0.25;
/**
 * Metres a wound-up blow or burst reaches beyond its weapon's range as it lands: the lunge that
 * carries it. Without it a target that merely backs away outran every blow during its wind-up, and
 * fleeing beat chasing (against D-08): the heavy blades lost 13 to 15 points against the roster.
 * With it the roster comes out within a few points of where it stood, and a dash, not a step back,
 * is what gets out of a blow's way. Measured: 400 pairs, 40 Expert duels each, before and after.
 */
export const WIND_UP_LUNGE = 0.35;

/** Whether an attack of this kind is wound up before it lands: everything but a shot and a ring. */
export function woundUp(kind: AttackType): boolean {
  return kind !== 'projectile' && kind !== 'burn';
}

/**
 * Handles an attack input for one fighter: a shot leaves at once; a blow, an aura or a burst is
 * wound up and lands `WIND_UP` later, on whoever is then in its reach and a lunge more. The
 * fighter goes on turning and moving meanwhile. A stagger holds the wind-up where it stands; it
 * does not undo it.
 */
export function attack(
  state: BattleState,
  map: TileMap,
  f: Fighter,
  input: FighterInput,
  dt = 0,
): void {
  // A burning ring is not a blow: it is raised and held rather than let off, and `stepBurn` runs
  // it. Falling through to here would swing a melee cone and put the ring on a cooldown it has no
  // use for, which would then keep the AI from ever raising it.
  if (f.attackType === 'burn' || !f.alive) return;
  if ((f.windup ?? 0) > 0) {
    if (f.stun > 0) return;
    f.windup = Math.max(0, f.windup! - dt);
    if (f.windup === 0) land(state, map, f);
    return;
  }
  if (f.stun > 0 || f.cooldown > 0 || !input.attack) return;
  f.cooldown = f.attackInterval;
  if (woundUp(f.attackType)) {
    state.events.push({ type: 'windup', fighter: f.id, kind: f.attackType });
    f.windup = WIND_UP;
    // With no wind-up (THE_DECISION_TABLE #2 reversed) the blow lands in the step it is pressed.
    if (f.windup > 0) return;
  }
  land(state, map, f);
}

/** An attack lands: a shot is let off, a blow or a contact weapon strikes whoever is in it now. */
function land(state: BattleState, map: TileMap, f: Fighter): void {
  state.events.push({ type: 'attack', fighter: f.id, kind: f.attackType });
  const target = fighterOf(state, opponentOf(state, f.id));
  switch (f.attackType) {
    case 'projectile': {
      const eye = eyePosition(f);
      const dir = forwardVector(f.yaw, f.pitch);
      const muzzle = {
        x: eye.x + dir.x * MUZZLE_OFFSET,
        y: eye.y + dir.y * MUZZLE_OFFSET,
        z: eye.z + dir.z * MUZZLE_OFFSET,
      };
      // Fired point-blank into a wall or the ground, the shot would be born inside it and could
      // fly on out of the far side. It breaks where it is born instead.
      if (shotStopped(map, eye, muzzle)) {
        state.events.push({ type: 'projectile-hit-world', at: muzzle, owner: f.id });
        return;
      }
      state.projectiles.push({
        id: state.nextProjectileId++,
        owner: f.id,
        pos: muzzle,
        vel: {
          x: dir.x * PROJECTILE_SPEED,
          y: dir.y * PROJECTILE_SPEED,
          z: dir.z * PROJECTILE_SPEED,
        },
        damage: f.damage,
        ttl: PROJECTILE_TTL,
        radius: PROJECTILE_RADIUS,
        turn: 0,
        steerable: f.steerableProjectile,
      });
      return;
    }
    case 'aura':
      if (inContact(map, f, target, WIND_UP_LUNGE)) strike(state, target, f.damage, f.id);
      return;
    case 'burst':
      if (inContact(map, f, target, WIND_UP_LUNGE)) strike(state, target, f.damage, f.id);
      // Releasing a burst has a cost, but it must not make a Phoenix defeat itself after five
      // attacks. Five percent keeps the thematic risk while leaving room for a sustained duel.
      hit(state, f, f.maxHp * BURST_SELF_DAMAGE, f.id, 'cost');
      return;
    default: {
      // Melee (and mimic, which copied the opponent's type at battle start): a cone in front.
      if (!target.alive) return;
      const dx = target.pos.x - f.pos.x;
      const dz = target.pos.z - f.pos.z;
      const dist = Math.hypot(dx, dz);
      if (dist > f.range + target.halfWidth + WIND_UP_LUNGE) return;
      const angle = Math.atan2(dx, dz);
      const diff = Math.abs(normalizeAngle(angle - f.yaw));
      if (diff > MELEE_ARC / 2) return;
      if (sightBlocked(map, eyePosition(f), centre(target))) return;
      strike(state, target, f.damage, f.id);
    }
  }
}

/**
 * What a blow actually lands, on a six-sided roll: a quarter of nominal at worst, double at best,
 * evenly spaced between. Two fighters of the same strength no longer trade identical blows until
 * the arithmetic decides it — which is what made a duel's outcome a foregone conclusion once hit
 * points were raised, and a whole board game deterministic with it.
 *
 * The mean is a shade above one (1.125), so fights run a little shorter than the nominal numbers
 * suggest; the balance pass is where that gets paid for.
 */
export const DAMAGE_DICE: readonly number[] = [0.25, 0.6, 0.95, 1.3, 1.65, 2];

/**
 * Rolls one blow. Drawn from the battle's own seeded state and written back, so the server and
 * every client roll the same dice in the same order — a duel is lockstep and cannot be left to
 * chance that is not shared.
 */
export function rollDamage(state: BattleState, damage: number): number {
  const rng = createRng(state.rng);
  const face = Math.min(DAMAGE_DICE.length - 1, Math.floor(rng.next() * DAMAGE_DICE.length));
  state.rng = rng.state();
  // Keep fractional health: rounding each hit made small percentage edits discontinuous.
  return Math.max(0, damage * DAMAGE_DICE[face]!);
}

/** A blow that has landed on someone: rolled, unlike a hazard or the cost of one's own burst. */
export function strike(state: BattleState, target: Fighter, damage: number, by: FighterId): void {
  const effective = effectDamage(state, target, rollDamage(state, damage), by);
  if (effective > 0) hit(state, target, effective, by, 'blow');
}

/** Applies the attacker's effect chance, the defender's protection and typed resistance. */
export function effectDamage(
  state: BattleState,
  target: Fighter,
  damage: number,
  by: FighterId,
): number {
  const source = fighterOf(state, by);
  const rng = createRng(state.rng);
  const takesEffect = rng.next() < source.effectChance * (1 - target.protection);
  state.rng = rng.state();
  if (!takesEffect) return 0;
  const resistance = target.resistances[source.damageType] ?? 0;
  // A 90% resistance must absorb 90%, including weak hits and rapid burning-ring ticks.
  return Math.max(0, damage * (1 - resistance));
}

/**
 * Takes health from a fighter. Only a blow staggers it (D-34): a ring's bite, the ground's burn
 * and a burst's cost to its owner would otherwise lock a fighter in place for as long as they
 * lasted, and a hazard underfoot would hold it there until it died.
 */
export function hit(
  state: BattleState,
  target: Fighter,
  damage: number,
  by: FighterId,
  source: HitSource,
): void {
  if (!target.alive || damage <= 0) return;
  if (state.pendingHits) {
    state.pendingHits.push({ target: target.id, damage, by, source });
    return;
  }
  target.hp = Math.max(0, target.hp - damage);
  if (source === 'blow') target.stun = Math.max(target.stun, HIT_STUN);
  const event: BattleEvent = { type: 'hit', target: target.id, damage, by, source };
  state.events.push(event);
  if (target.hp === 0) fall(state, target);
}

/** A fighter's death: whatever it was holding up goes down with it. */
export function fall(state: BattleState, f: Fighter): void {
  f.alive = false;
  f.burning = false;
  state.events.push({ type: 'death', fighter: f.id });
}

/** Advances projectiles: world collision, fighter hits, expiry. */
export function stepProjectiles(
  state: BattleState,
  map: TileMap,
  dt: number,
  inputs: Partial<Record<FighterId, FighterInput>> = {},
): void {
  const survivors: Projectile[] = [];
  for (const p of state.projectiles) {
    p.ttl -= dt;
    if (p.steerable) {
      const wanted = Math.max(-1, Math.min(1, inputs[p.owner]?.steer ?? 0));
      const before = p.turn ?? 0;
      const after = Math.max(
        -PROJECTILE_STEER_LIMIT,
        Math.min(PROJECTILE_STEER_LIMIT, before + wanted * PROJECTILE_STEER_RATE * dt),
      );
      const bend = after - before;
      if (bend !== 0) {
        const cos = Math.cos(bend);
        const sin = Math.sin(bend);
        const x = p.vel.x * cos + p.vel.z * sin;
        const z = p.vel.z * cos - p.vel.x * sin;
        p.vel.x = x;
        p.vel.z = z;
        p.turn = after;
      }
    }
    const next: Vec3 = {
      x: p.pos.x + p.vel.x * dt,
      y: p.pos.y + p.vel.y * dt,
      z: p.pos.z + p.vel.z * dt,
    };
    const target = fighterOf(state, opponentOf(state, p.owner));
    if (target.alive && segmentHitsFighter(p.pos, next, target, p.radius)) {
      strike(state, target, p.damage, p.owner);
      continue;
    }
    // Shots are the one thing that still flies in three dimensions: a shot stops against a tile
    // taller than the altitude it is passing at, and against the ground itself. Unlike a sight
    // line between two fighters, the cell it is flying *into* counts — that is the tile it hits.
    const altitude = Math.min(p.pos.y, next.y);
    if (
      next.y <= 0 ||
      map.tileAt(next.x, next.z).height >= altitude ||
      map.blocksSight(p.pos.x, p.pos.z, next.x, next.z, altitude)
    ) {
      state.events.push({ type: 'projectile-hit-world', at: next, owner: p.owner });
      continue;
    }
    p.pos = next;
    if (p.ttl > 0) survivors.push(p);
  }
  state.projectiles = survivors;
}

/** How far ahead of the eye a shot is born, clear of the shooter's own body. */
export const MUZZLE_OFFSET = 0.6;

/** Whether a shot from the eye is stopped before it reaches its muzzle: by a tile or the ground. */
function shotStopped(map: TileMap, eye: Vec3, muzzle: Vec3): boolean {
  const altitude = Math.min(eye.y, muzzle.y);
  return (
    muzzle.y <= 0 ||
    map.tileAt(muzzle.x, muzzle.z).height >= altitude ||
    map.blocksSight(eye.x, eye.z, muzzle.x, muzzle.z, altitude)
  );
}

/**
 * Whether an aura or a burst reaches a target: within range across the floor, and with no wall
 * between them, the sight rule the blade and the ring already follow.
 */
/** Whether a contact weapon reaches the target: its range, and `extra` for a lunge. */
function inContact(map: TileMap, f: Fighter, target: Fighter, extra = 0): boolean {
  return (
    target.alive &&
    horizontalDistance(centre(f), centre(target)) <= f.range + extra &&
    !sightBlocked(map, eyePosition(f), centre(target))
  );
}

function segmentHitsFighter(a: Vec3, b: Vec3, f: Fighter, radius: number): boolean {
  // Sample the segment against the fighter's box, grown by the projectile radius.
  const steps = 4;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = a.x + (b.x - a.x) * t;
    const y = a.y + (b.y - a.y) * t;
    const z = a.z + (b.z - a.z) * t;
    if (
      Math.abs(x - f.pos.x) <= f.halfWidth + radius &&
      Math.abs(z - f.pos.z) <= f.halfWidth + radius &&
      y >= f.pos.y - radius &&
      y <= f.pos.y + f.height + radius
    )
      return true;
  }
  return false;
}

/**
 * An angle folded into [−π, π]. Play only ever folds about a turn (two facings subtracted, aim
 * slip on top), and the loop folds those exactly as it always has: the calibrated odds were
 * measured on its results. Anything further out takes one remainder first, so no value, however
 * large, makes the loop walk it back a turn at a time for ever.
 */
export function normalizeAngle(a: number): number {
  if (Math.abs(a) > 4 * Math.PI) a %= 2 * Math.PI;
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a < -Math.PI) a += 2 * Math.PI;
  return a;
}
